/**
 * Connect an assistant — the user-facing end of the MCP server.
 *
 * The server has worked since v1.59, but the only way to point anything at it
 * was to know the URL by heart, which meant it existed for exactly one person.
 * This is the panel that makes it a feature rather than a deployment detail.
 *
 * The URL is derived from `window.location.origin`, never hardcoded, so a
 * deploy preview hands out its own address instead of quietly sending someone
 * to production.
 *
 * Two views (CLAUDE.md §2): on desktop the address and its Copy button sit on
 * one line; on a phone the button drops beneath the address, because a
 * non-wrapping row containing a full URL is exactly the thing that makes iOS
 * Safari zoom the whole page out. The address itself scrolls inside its own
 * box rather than stretching the dialog.
 */

import { useState } from 'react'
import { Copy, Check, Plug, ExternalLink } from 'lucide-react'
import { Button } from '@/components/ui/button'
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card'

export function AssistantConnect() {
  const [copied, setCopied] = useState(false)
  const url = `${window.location.origin}/mcp`

  const copy = async () => {
    try {
      await navigator.clipboard.writeText(url)
      setCopied(true)
      setTimeout(() => setCopied(false), 2000)
    } catch {
      // Clipboard is blocked in some embedded browsers and over plain http.
      // The address is visible and selectable either way, so there is nothing
      // to recover from — just don't claim success.
      setCopied(false)
    }
  }

  return (
    <Card>
      <CardHeader>
        <CardTitle className="flex items-center gap-2 text-base">
          <Plug className="h-4 w-4" />
          Connect an assistant
        </CardTitle>
        <CardDescription>
          Let an AI assistant read your tasks and add them by voice or chat. Works with
          Claude, and with any other app that supports MCP connectors.
        </CardDescription>
      </CardHeader>
      <CardContent className="space-y-4">
        <div>
          <label className="text-sm font-medium" htmlFor="mcp-url">
            Your connector address
          </label>
          {/* Stacks below md — a full URL on one non-wrapping line is what
              makes iOS zoom the page out. */}
          <div className="mt-1.5 flex flex-col gap-2 md:flex-row md:items-center">
            <input
              id="mcp-url"
              readOnly
              value={url}
              onFocus={(e) => e.currentTarget.select()}
              className="min-w-0 flex-1 rounded-md border bg-muted px-3 py-2 font-mono text-sm"
            />
            <Button
              variant="outline"
              onClick={copy}
              className="h-11 shrink-0 md:h-10"
            >
              {copied ? <Check className="mr-2 h-4 w-4" /> : <Copy className="mr-2 h-4 w-4" />}
              {copied ? 'Copied' : 'Copy'}
            </Button>
          </div>
        </div>

        <div className="space-y-1.5 text-sm text-muted-foreground">
          <p className="font-medium text-foreground">To connect Claude:</p>
          <ol className="list-decimal space-y-1 pl-5">
            <li>Settings → Connectors → Add custom connector</li>
            <li>Paste the address above</li>
            <li>
              Sign in with the same account you use here. Your assistant then sees the
              same tasks this app does.
            </li>
          </ol>
        </div>

        <div className="rounded-md border border-amber-500/40 bg-amber-500/5 p-3 text-sm">
          <p className="font-medium">Before you connect</p>
          <p className="mt-1 text-muted-foreground">
            Connecting gives that assistant the same access to your stored data that this
            app has, so only connect one you trust with your task list. It can read your
            tasks and plan, add and complete them, and reschedule your days. It cannot see
            events from your connected calendars — those never leave this device. You can
            disconnect at any time from your assistant, or by revoking TrackMind in your
            account&rsquo;s security settings.
          </p>
        </div>

        <a
          href="https://github.com/RajeshDM/task_manager_serverless/blob/main/docs/MCP-SETUP.md"
          target="_blank"
          rel="noreferrer"
          className="inline-flex items-center gap-1.5 text-sm underline hover:text-foreground"
        >
          Setup details and troubleshooting
          <ExternalLink className="h-3 w-3" />
        </a>
      </CardContent>
    </Card>
  )
}
